import { Component, Inject, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { debounceTime, distinctUntilChanged, startWith } from 'rxjs/operators';
import { Project } from '../../classes/project.class';
import { User } from '../../classes/user.class';
import { CodaltComponent } from '../../codalt.component';
import { ProjectEditDialogComponent } from '../../pages/projects/project-edit-dialog/project-edit-dialog.component';
import { ProjectService } from '../../services/project.service';

export interface SelectProjectDialogData {
    executor: User;
    errorMessage?: string;
    currentProjectIds: string[];
    buttonText?: string;
}

export interface SelectProjectDialogResult {
    project: Project;
    executorId: number;
}

@Component({
    selector: 'app-select-project-dialog',
    templateUrl: './select-project-dialog.component.html',
    styleUrl: './select-project-dialog.component.scss',
    standalone: false,
})
export class SelectProjectDialogComponent extends CodaltComponent implements OnInit {
    searchControl = new FormControl('');
    allProjects: Project[] = [];
    filteredProjects: Project[] = [];
    isLoading = true;

    constructor(
        private dialogRef: MatDialogRef<SelectProjectDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: SelectProjectDialogData,
        private projectService: ProjectService,
        private dialog: MatDialog
    ) {
        super();
    }

    ngOnInit(): void {
        this.setupSearch();
        // Initial load will be triggered by the search control's startWith('')
    }

    private setupSearch(): void {
        this.subscriptions.add(
            this.searchControl.valueChanges
                .pipe(startWith(''), debounceTime(300), distinctUntilChanged())
                .subscribe(searchTerm => {
                    this.searchProjects(searchTerm || '');
                })
        );
    }

    private searchProjects(searchTerm: string): void {
        this.isLoading = true;
        this.subscriptions.add(
            this.projectService.searchProjects(searchTerm, {}, true).subscribe({
                next: projects => {
                    // Filter out projects that are already assigned to this executor
                    this.filteredProjects = (projects || []).filter(
                        project => !this.data.currentProjectIds.includes(project.afas_project_id)
                    );
                    this.isLoading = false;
                },
                error: error => {
                    console.error('Error searching projects:', error);
                    this.isLoading = false;
                },
            })
        );
    }

    onProjectSelect(project: Project): void {
        const result: SelectProjectDialogResult = {
            project: project,
            executorId: this.data?.executor?.id || 0,
        };
        this.dialogRef.close(result);
    }

    onCancel(): void {
        this.dialogRef.close();
    }

    trackByProjectId(index: number, project: Project): string {
        return project.afas_project_id;
    }

    addProject(): void {
        this.dialog.open(ProjectEditDialogComponent, {
            data: {
                executor_id: this.data.executor.id,
            },
        });

        this.dialogRef.afterClosed().subscribe(result => {
            const res = {
                project: result,
                executorId: this.data?.executor?.id || 0,
            };
            this.dialogRef.close(res);
        });
    }
}
